/**
 * Gift cards — Overview. Opened from the "Overview" button on the Gift cards
 * box: gift-card SALES by outlet (every till that sells them — the shops,
 * Customer Services, booking offices, Groups) for all time, the selected
 * season and the current month, from the same gift-card ledger the box reads.
 * Online (Shopify) gift-card products are shown on their own line: those cards
 * are loaded at Pickering Retail, whose ledger figure already includes them,
 * so the line is information rather than an addition to the total.
 */
import { useEffect, useState } from "react";
import type { GiftCardOverviewResponse } from "./salesInsightsTypes";
import { pounds, savePdf, sectionTitle, startPdf, table } from "./salesPdf";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : ym;
}
function whenLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

export interface GiftCardOverviewModalProps {
  fiscalYear: number;
  roundUp: boolean;
  onClose: () => void;
}

export function GiftCardOverviewModal({ fiscalYear, roundUp, onClose }: GiftCardOverviewModalProps) {
  const [data, setData] = useState<GiftCardOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const month = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    fetch(`/api/dashboards/sales/gift-cards/overview?fiscalYear=${fiscalYear}&month=${month}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error((b as { error?: string }).error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<GiftCardOverviewResponse>;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [fiscalYear, month]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const gbp = (n: number) => pounds(n, roundUp);
  const cell = (p: { n: number; amount: number }) => (
    <>
      <td className="ta-right">{gbp(p.amount)}</td>
      <td className="ta-right sales-gco-n">{p.n ? p.n.toLocaleString() : "–"}</td>
    </>
  );

  return (
    <div className="sales-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sales-modal sales-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-head">
          <div>
            <div className="sales-modal-title">Gift cards — sales by outlet</div>
            <div className="sales-modal-sub">
              All time · season {data?.fiscalYearLabel ?? ""} · {monthLabel(month)} — gift-card ledger (cards bought), all outlets
            </div>
          </div>
          <div className="sales-modal-actions">
            <button type="button" className="sales-gc-btn" onClick={() => data && downloadGiftCardOverviewPdf(data, roundUp)} disabled={!data}>PDF</button>
            <button type="button" className="sales-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div className="sales-modal-body">
          {error ? (
            <div className="sales-empty sales-empty--error">Couldn’t load gift-card sales: {error}</div>
          ) : !data ? (
            <div className="sales-empty">Loading…</div>
          ) : data.rows.length === 0 ? (
            <div className="sales-empty">The gift-card ledger has no sales rows.</div>
          ) : (
            <>
              <table className="sales-bd-table sales-gco-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>Outlet</th>
                    <th colSpan={2} className="ta-right">All time</th>
                    <th colSpan={2} className="ta-right">Season {data.fiscalYearLabel}</th>
                    <th colSpan={2} className="ta-right">{monthLabel(data.month)}</th>
                  </tr>
                  <tr>
                    <th className="ta-right">Sales</th><th className="ta-right sales-gco-n">Cards</th>
                    <th className="ta-right">Sales</th><th className="ta-right sales-gco-n">Cards</th>
                    <th className="ta-right">Sales</th><th className="ta-right sales-gco-n">Cards</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.outlet}>
                      <td title={r.tills.length ? `Tills ${r.tills.join(", ")}` : undefined}>{r.outlet}</td>
                      {cell(r.allTime)}{cell(r.season)}{cell(r.month)}
                    </tr>
                  ))}
                  <tr className="sales-gco-total">
                    <td>Total (ledger)</td>
                    {cell(data.totals.allTime)}{cell(data.totals.season)}{cell(data.totals.month)}
                  </tr>
                  <tr className="sales-gco-online" title={data.online.note}>
                    <td>{data.online.outlet}{data.online.from ? ` — from ${data.online.from.slice(0, 7)}` : ""}</td>
                    {cell(data.online.allTime)}{cell(data.online.season)}{cell(data.online.month)}
                  </tr>
                </tbody>
              </table>
              <p className="sales-gco-note">
                {data.online.note} Ledger as at {whenLabel(data.ledger_generated_at)}.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function downloadGiftCardOverviewPdf(data: GiftCardOverviewResponse, roundUp: boolean): void {
  const gbp = (n: number) => pounds(n, roundUp);
  const { doc, y: y0 } = startPdf(
    "Gift cards — sales by outlet",
    `All time · season ${data.fiscalYearLabel} (${data.season.from} to ${data.season.to}) · ${monthLabel(data.month)} · gift-card ledger as at ${whenLabel(data.ledger_generated_at)}`
  );
  const y = sectionTitle(doc, "Cards bought, by the outlet that sold them", y0);
  const r = (o: { outlet: string; allTime: { n: number; amount: number }; season: { n: number; amount: number }; month: { n: number; amount: number } }) =>
    [o.outlet, gbp(o.allTime.amount), o.allTime.n.toLocaleString(), gbp(o.season.amount), o.season.n.toLocaleString(), gbp(o.month.amount), o.month.n.toLocaleString()];
  table(doc, {
    startY: y,
    head: [[
      { content: "Outlet", rowSpan: 2, styles: { halign: "left" } },
      { content: "All time", colSpan: 2 },
      { content: `Season ${data.fiscalYearLabel}`, colSpan: 2 },
      { content: monthLabel(data.month), colSpan: 2 },
    ], ["Sales", "Cards", "Sales", "Cards", "Sales", "Cards"]],
    body: [...data.rows.map(r), r({ outlet: "Total (ledger)", ...data.totals }), r(data.online)],
    columnStyles: { 0: { halign: "left" } },
    didParseCell: (d) => {
      if (d.section === "body" && d.row.index === data.rows.length) d.cell.styles.fontStyle = "bold";
      if (d.section === "body" && d.row.index === data.rows.length + 1) d.cell.styles.textColor = [110, 110, 110];
    },
  });
  const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  doc.text(data.online.note, 10, Math.min(finalY + 6, doc.internal.pageSize.getHeight() - 10), { maxWidth: doc.internal.pageSize.getWidth() - 20 });
  savePdf(doc, `gift-card-sales-by-outlet-${data.fiscalYearLabel.replace("/", "-")}.pdf`);
}
